import { useReviewsWizard } from '../ReviewsWizardProvider'
import { useSelector } from '@xstate/react'
import { ReactElement, useCallback, useRef } from 'react'
import { EventId } from '../machineTypes'
import { Button } from '../../Button'
import { ConditionalWrapper } from '../../../common/ConditionalWrapper/ConditionalWrapper'
import { Field } from '../AddReviewers/Field'
import { RecipientSelectorField } from '../RecipientsSelectorField/RecipientsSelectorField'
import Styles from './SuggestersSelection.module.scss'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { RecipientsSelectorValueType } from 'types'
import { ourUuid } from '../../../utils'
import { Modal } from 'almanac-ui/Modal'

interface Props {
  newEmails: string[]
}

export const SuggestersSelection = ({ newEmails }: Props) => {
  const { service, isLoading } = useReviewsWizard()
  const suggesters = useSelector(service, (state) => state.context.suggesters)
  const title = useSelector(service, (state) => state.context.title)
  const message = useSelector(service, (state) => state.context.message)
  const messageRef = useRef<HTMLTextAreaElement>(null)

  const handleSubmit = useCallback(() => {
    if (suggesters.length < 1 || title.length < 1) {
      return
    }
    service.send(EventId.CONTINUE, { newEmails })
  }, [suggesters.length, title.length, service, newEmails])

  return (
    <>
      <Modal.Header title="Configure your suggestion request" />
      <Modal.Content>
        <RecipientSelectorField
          includeGroups={false}
          id="suggesters-select"
          label="Suggester:"
          placeholder="Add person or external email"
          onChange={(recipients) => {
            service.send(EventId.SELECT_SUGGESTERS, { suggesters: [recipients.pop() || []] })
            messageRef.current?.focus()
          }}
          recipients={suggesters}
        />
        <RecipientSelectorField
          disabled
          id="approvers-select"
          label="Approvers:"
          onChange={() => {}}
          recipients={[
            {
              type: RecipientsSelectorValueType.AccessMemberGroup,
              value: {
                id: ourUuid(),
                label: 'Document Editors',
                name: 'Document Editors',
                members: [],
                channels: [],
              },
            },
          ]}
        />
        <Field>
          <label htmlFor="subject" className={Styles.fieldLabel}>
            Suggestions Name:
          </label>
          <input
            id="subject"
            onChange={(event) => service.send(EventId.SET_TITLE, { title: event.target.value })}
            value={title}
            className={Styles.reviewNameInput}
          />
        </Field>
        <Field>
          <textarea
            className={Styles.message}
            placeholder="Type your message here..."
            value={message}
            onChange={(event) => service.send(EventId.SET_MESSAGE, { message: event.target.value })}
            rows={5}
            ref={messageRef}
          />
        </Field>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Button variant="tertiary" size="xl" onPress={() => service.send(EventId.BACK)} isDisabled={isLoading}>
            Back
          </Button>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <ConditionalWrapper
            condition={suggesters.length < 1}
            wrapper={(children) => (
              <Tooltip placement="top" label="You must select suggesters first">
                {children as ReactElement}
              </Tooltip>
            )}
          >
            <Button onPress={handleSubmit} data-heap-id="submit-suggestions-request" isDisabled={isLoading} size="xl">
              Send Request
            </Button>
          </ConditionalWrapper>
        </Modal.FooterContent>
      </Modal.Footer>
    </>
  )
}
